import { Box, Typography } from '@mui/material'
import { formatCurrency, formatInterestRate } from '../../../utils'
import {
  BillPreviewRetainerInfoBox,
  BillPreviewSummarySection,
  BillPreviewTotalAmountBox
} from '../../../theme/styleComponents'
import { flexJustifySpaceBetween } from '../../../theme/styleConstants'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  BillingType,
  BillType,
  FetchBillDetailsQuery,
  FetchPublicBillDetailsQuery,
  useFetchRemainingFlatFeeLazyQuery
} from '../../../../generated'
import { getRetainerPaymentInfo, isContingencyCase, isHourlyCase } from '../../../utils/billing'
import BillPayments from './BillPayments'
import {
  CalculateClientBillDataResult,
  UseFetchBillInterestResult,
  UseFetchOutstandingBillsResult
} from '../../../interfaceTypes'
import useGetClientTrustBalance from '../../../hooks/useGetClientTrustBalance'
import useGetClientCreditBalance from '../../../hooks/useGetClientCreditBalance'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import useFetchHybridCaseFlatFeeConsumed from '../../../hooks/useFetchHybridCaseFlatFeeConsumed'

type ProtectedBill = NonNullable<FetchBillDetailsQuery['fetchBillDetails']['bill']>
type PublicBill = NonNullable<FetchPublicBillDetailsQuery['fetchPublicBillDetails']['bill']>
type Client = {
  splitShare: number | null | undefined
  clientId: string | undefined
}

interface BillPreviewSummaryProps {
  bill: ProtectedBill | PublicBill | undefined
  isFinalBill: boolean | undefined
  client: Client | null | undefined
  selectedAccessToken?: { token: string | undefined; amount: number | undefined }
  clientData?: CalculateClientBillDataResult
  interestData?: UseFetchBillInterestResult
  outstandingBillsData?: UseFetchOutstandingBillsResult
  onReadyToPrint?: (isReady: boolean) => void
}

const BillPreviewSummary: FC<BillPreviewSummaryProps> = ({
  bill,
  client,
  selectedAccessToken,
  isFinalBill,
  clientData,
  interestData,
  outstandingBillsData,
  onReadyToPrint
}) => {
  const [remainingFlatFee, setRemainingFlatFee] = useState(0)
  const { flatFeeConsumed, loading: isLoadingFlatFeeConsumed } = useFetchHybridCaseFlatFeeConsumed({
    caseId: bill?.case?.id || '',
    billingType: bill?.case?.billing?.billingType || null,
    isFlatFeeHoursExhausted: !!bill?.case?.billing?.isFlatFeeHoursExhausted
  })

  const [fetchRemainingFlatFee, { loading: isLoadingRemainingFlatFee }] = useFetchRemainingFlatFeeLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: data => {
      if (data.fetchRemainingFlatFee.remainingFlatFee) {
        setRemainingFlatFee(data.fetchRemainingFlatFee.remainingFlatFee)
      }
    }
  })

  const billData = useMemo(() => {
    const { discount, case: caseData, paymentAllocations, status } = bill || {}

    return {
      status,
      discount,
      caseData,
      paymentAllocations,
      discountPercentage: bill?.discountPercentage,
      billing: caseData?.billing
    }
  }, [bill])
  const { discount, caseData, discountPercentage, billing } = billData

  const { splitShare: clientSplitShare, clientId } = client || {}

  const showPaymentsDetail = isFinalBill && !!clientData?.paymentAllocations?.length
  const showSplitBill = isFinalBill && caseData?.billing?.splitBillEnabled
  const showInterest = isFinalBill && !!interestData?.interestAccrued
  const isFlatFeeCase = bill?.type === BillType.Bill && billing?.billingType === BillingType.FlatFee
  const showFlatFeeConsumed =
    bill?.type === BillType.Bill && billing?.billingType === BillingType.Hybrid && !billing?.isFlatFeeHoursExhausted
  const payOffAmount = (isFinalBill ? remainingFlatFee : bill?.payOffAmount) || 0
  const previousPrincipalBalance =
    outstandingBillsData?.outstandingBills?.reduce((acc, bill) => acc + bill.principalBalance, 0) || 0
  const previousInterest = outstandingBillsData?.outstandingBills?.reduce((acc, bill) => acc + bill.interestDue, 0) || 0
  const showTrustBalance = useMemo(
    () => bill?.type === BillType.Bill && billing && (isHourlyCase(billing) || isContingencyCase(billing)),
    [billing, bill?.type]
  )

  const showWrittenOffPrincipal = isFinalBill && Number(clientData?.writtenOff) !== 0

  const { trustBalance, loading: isLoadingTrustBalance } = useGetClientTrustBalance({
    showTrustBalance: !!showTrustBalance,
    caseId: bill?.case?.id,
    clientId: clientId,
    token: selectedAccessToken?.token
  })

  const { creditBalance, loading: isLoadingCreditBalance } = useGetClientCreditBalance({
    caseId: bill?.case?.id,
    clientId: clientId,
    token: selectedAccessToken?.token
  })

  useEffect(() => {
    if (bill?.case?.id && bill?.case?.billing?.billingType === BillingType.FlatFee) {
      fetchRemainingFlatFee({
        variables: {
          fetchRemainingFlatFeeInput: { caseId: bill.case.id }
        }
      })
    }
  }, [bill?.case?.id, bill?.case?.billing?.billingType, fetchRemainingFlatFee])

  useEffect(() => {
    if (onReadyToPrint) {
      const isDataLoading = isLoadingRemainingFlatFee || isLoadingTrustBalance || isLoadingCreditBalance
      onReadyToPrint(!isDataLoading)
    }
  }, [isLoadingRemainingFlatFee, isLoadingTrustBalance, isLoadingCreditBalance, onReadyToPrint])

  return (
    <BillPreviewSummarySection>
      <BillPreviewRetainerInfoBox>
        {isFlatFeeCase && (
          <>
            <Typography>
              <b>Retainer/Payment Info:</b> {getRetainerPaymentInfo(billing)}
            </Typography>

            {!!payOffAmount && (
              <Typography fontWeight={700}>
                If you wish to pay the entire balance in full Pay Off Amount: {formatCurrency(payOffAmount)}
              </Typography>
            )}
          </>
        )}

        {showTrustBalance && !isLoadingTrustBalance && (
          <Typography fontWeight={700} sx={{ mt: { md: 'auto' } }}>
            You have {formatCurrency(trustBalance)} remaining in Client Funds
          </Typography>
        )}

        {creditBalance > 0 && !isLoadingCreditBalance && (
          <Typography fontWeight={700} sx={{ mt: { md: 'auto' } }}>
            You have {formatCurrency(creditBalance)} remaining in Credit Balance
          </Typography>
        )}

        {showFlatFeeConsumed && !isLoadingFlatFeeConsumed && (
          <Typography fontWeight={700} sx={{ mt: { md: 'auto' } }}>
            You have used {formatCurrency(Math.min(flatFeeConsumed, billing?.flatFee || 0))} from your total flat fee of{' '}
            {formatCurrency(billing?.flatFee || 0)}.
          </Typography>
        )}
      </BillPreviewRetainerInfoBox>

      <BillPreviewTotalAmountBox>
        <Box sx={{ ...flexJustifySpaceBetween }}>
          <Typography fontSize="12px">Total</Typography>
          <Typography fontSize="12px">{clientData?.subtotal && formatCurrency(clientData?.subtotal)}</Typography>
        </Box>
        {!!discount && (
          <Box sx={{ mt: 1.5 }}>
            <Typography fontWeight={700} mb={0.5}>
              Discounts
            </Typography>
            <Box sx={{ ...flexJustifySpaceBetween }}>
              <Typography>{discountPercentage}%</Typography>
              <Typography>{`-${formatCurrency(discount || 0)}`}</Typography>
            </Box>
          </Box>
        )}
        {showSplitBill && (
          <>
            <Box sx={{ ...flexJustifySpaceBetween }} mt={2}>
              <Typography>Total Bill Payable</Typography>
              <Typography>{formatCurrency(bill?.amount || 0)}</Typography>
            </Box>
            <Box sx={{ ...flexJustifySpaceBetween }} mt={0.5}>
              <Typography>Your Share ({clientSplitShare}%)</Typography>
              <Typography>{formatCurrency(selectedAccessToken?.amount || 0)}</Typography>
            </Box>
          </>
        )}

        {showInterest ? (
          <Box sx={{ ...flexJustifySpaceBetween }} mt={2}>
            <Typography> Interest on this Invoice ({formatInterestRate(interestData.rate || 0)})</Typography>
            <Typography>{formatCurrency(interestData.interestAccrued || 0)}</Typography>
          </Box>
        ) : null}

        {!!outstandingBillsData?.previousBalance && (
          <>
            <Box sx={{ ...flexJustifySpaceBetween }} mt={2}>
              <Typography>Previous Balance</Typography>
              <Typography>{formatCurrency(previousPrincipalBalance)}</Typography>
            </Box>
            {!!previousInterest && (
              <Box sx={{ ...flexJustifySpaceBetween }} mt={0.5}>
                <Typography>Previous Interest</Typography>
                <Typography>{formatCurrency(previousInterest)}</Typography>
              </Box>
            )}
          </>
        )}

        {showPaymentsDetail && <BillPayments paymentAllocations={clientData?.paymentAllocations || []} />}

        {showWrittenOffPrincipal && (
          <Box sx={{ ...flexJustifySpaceBetween }} mt={2}>
            <Typography>Written Off</Typography>
            <Typography>{formatCurrency((clientData?.writtenOff || 0) * -1)}</Typography>
          </Box>
        )}

        <Box sx={{ ...flexJustifySpaceBetween }} mt={2}>
          <Typography fontWeight={700}>Payable Amount</Typography>
          <Typography fontWeight={700}>{formatCurrency(clientData?.payableAmount || 0)}</Typography>
        </Box>
      </BillPreviewTotalAmountBox>
    </BillPreviewSummarySection>
  )
}

export default BillPreviewSummary
